import React, { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { addVendor } from "../../Api/Vendors/Vendors";
import "../../Customers.css";
import useVendorReferrals from "../../hooks/useVendorReferrals";
import { Formik } from "formik";
import VendorForm from "../VendorForm/VendorForm";
import { vendorSchema } from "../../util/schema";
import { mapToCreateVendor } from "../../util/mapper";
import { DuplicateNameDialog } from "src/components/DuplicateNameDialog/DuplicateNameDialog.tsx";
import { findMatchingNames } from "src/utils/nameValidation.ts";

const CreateVendor = ({ getData, existingVendors = [] }) => {
  const [referralAll, setReferralAll] = useState([]);
  const [dialogState, setDialogState] = useState({ open: false, matches: [], matchType: "fuzzy" });
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const { data: vendorReferrals, loading: loadingReferrals } = useVendorReferrals();

  const existingVendorNames = useMemo(() => {
    return existingVendors.map(v => ({ id: v.VENDOR_ID, name: v.VENDOR_NAME }));
  }, [existingVendors]);

  useEffect(() => {
    setReferralAll(
      vendorReferrals?.map(referral => {
        return { label: referral.NAME, value: referral.ID.toString() };
      }),
    );
  }, [loadingReferrals, vendorReferrals]);

  const initialValues = {
    REFERRAL: "",
    REFERRAL_COMMISSION_RATE: "",
    REFERRAL_COMMISSION_DURATION: "",
    REFERRAL_PARTNER_ID: "",
    REFERRAL_PAYMENT_METHOD: "",
    REFERRAL_PARTNER_TYPE: "",
    REFERRAL_CUSTOM_COMMISSION_RATE: "",
    REFERRAL_2: "",
    REFERRAL_2_COMMISSION_RATE: "",
    REFERRAL_2_COMMISSION_DURATION: "",
    REFERRAL_2_PARTNER_ID: "",
    REFERRAL_2_PAYMENT_METHOD: "",
    REFERRAL_2_PARTNER_TYPE: "",
    REFERRAL_2_CUSTOM_COMMISSION_RATE: "",
    SPECIFIC_POINT_OF_CONTACT_NAME: "",
    SPECIFIC_POINT_OF_CONTACT_EMAIL: "",
    VENDOR_NAME: "",
    TIER: "",
    INITIAL_COMMISSION_RATE: "",
    FINAL_COMMISSION_RATE: "",
    ENGAGEMENT_FEE: "",
    CURRENT_RECOVERY: "",
    NOTES: "",
    RECOVERY_THRESHOLD: "",
    AGREEMENT_SIGNED_DATE: "",
    KICK_OFF: "",
    PHONE: "",
    BILLING_NAME: "",
    BILLING_EMAIL: "",
    BILLING_ENTITY: "",
    VENDOR_TYPE: "",
    CLIENT_SUCCESS_MANAGER_NAME: "",
    CLIENT_SUCCESS_MANAGER_EMAIL: "",
  };

  const handleVendorSubmission = async (values, { resetForm, setSubmitting }) => {
    if (values.VENDOR_NAME) {
      const matchResult = findMatchingNames(values.VENDOR_NAME, existingVendorNames);

      if (matchResult.fuzzyMatches.length > 0) {
        setPendingSubmission({ values, resetForm, setSubmitting });
        setDialogState({
          open: true,
          matches: matchResult.fuzzyMatches,
          matchType: "fuzzy",
        });
        setSubmitting(false);
        return;
      }
    }

    await submitVendor(values, resetForm);
    setSubmitting(false);
  };

  const submitVendor = async (values, resetForm) => {
    const mappedData = mapToCreateVendor(values);
    const result = await addVendor(mappedData, toast, getData);

    if (result.success) {
      resetForm();
    }

    return result;
  };

  const handleDialogProceed = async () => {
    if (!pendingSubmission) return;

    const { values, resetForm, setSubmitting } = pendingSubmission;

    setDialogState({ open: false, matches: [], matchType: "fuzzy" });
    setSubmitting(true);

    await submitVendor(values, resetForm);

    setSubmitting(false);
    setPendingSubmission(null);
  };

  const handleDialogClose = () => {
    setDialogState({ open: false, matches: [], matchType: "fuzzy" });
    setPendingSubmission(null);
  };

  return (
    <>
      <div className="container mt-4">
        <Formik
          validateOnMount={true}
          initialValues={initialValues}
          onSubmit={handleVendorSubmission}
          validationSchema={vendorSchema}
        >
          {({ isSubmitting, dirty, isValid, values }) => (
            <VendorForm
              isSubmitting={isSubmitting}
              dirty={dirty}
              isValid={isValid}
              values={values}
              referralAll={referralAll}
              setReferralAll={setReferralAll}
              disableCurrentRecovery={false}
              existingVendors={existingVendors}
            />
          )}
        </Formik>
      </div>

      {/* Fuzzy match dialog */}
      <DuplicateNameDialog
        open={dialogState.open}
        onClose={handleDialogClose}
        onProceed={handleDialogProceed}
        matchType={dialogState.matchType}
        entityType="vendor"
        proposedName={pendingSubmission?.values?.VENDOR_NAME || ""}
        matches={dialogState.matches}
      />
    </>
  );
};

export default CreateVendor;
